import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, model, signal} from '@angular/core';
import {AuthService} from '../../security/auth.service';
import {JsonPipe, SlicePipe} from '@angular/common';
import {AppComponent} from '../../app.component';
import {StringUtils} from '../../lib/strings';
import {WjComboBox, WjInputModule} from '@mescius/wijmo.angular2.input';
import {changeCulture, culture, Globalize} from '@mescius/wijmo';
import {AppDevToolsService} from '../../service/app-devtools.service';
import {AccessControlService} from '../../security/access-control.service';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjTabStatePersistDirective} from '../../directives/wjtab-state-persist.directive';
import {FormsModule} from '@angular/forms';
import {AppConstants} from '../../app.constants';

class LocaleInDropDown {
    constructor(public name: string, public key: string) {
    }

    static Locales =  [
        new LocaleInDropDown('Chinese', 'zh-CN'),
        new LocaleInDropDown('English (US)','en'),
        new LocaleInDropDown('English (UK)', 'en-GB'),
        new LocaleInDropDown('French', 'fr'),
        new LocaleInDropDown('German', 'de'),
        new LocaleInDropDown('Italian', 'it'),
        new LocaleInDropDown('Japanese', 'ja'),
        new LocaleInDropDown('Korean', 'ko'),
        new LocaleInDropDown('Portuguese', 'pt'),
        new LocaleInDropDown('Russian', 'ru'),
        new LocaleInDropDown('Spanish', 'es'),
    ];

    static lookupByKey(key: string): LocaleInDropDown {
        return this.Locales.find(l => l.key === key) ?? new LocaleInDropDown(key, key);
    }
}

@Component({
    selector: 'app-user-details',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [SlicePipe, JsonPipe, WjInputModule, WjNavModule, WjTabStatePersistDirective, FormsModule],
    templateUrl: './user-details.component.html',
    styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent {
    protected authService = inject(AuthService);
    protected accessControlSvc = inject(AccessControlService);

    protected readonly lobOffices = computed(() => {
        return this.accessControlSvc.userContext().lobOfficePermissions.map(p => `${p.lobKey}-${p.officeKey}`);
    });

    protected readonly devToolsSvc = inject(AppDevToolsService)

    protected locales = LocaleInDropDown.Locales;
    protected selectedLocale = LocaleInDropDown.lookupByKey(AppConstants.getUserLocale());
    protected localeChanged = signal(false);

    constructor() {
    }

    protected async copyIdToken() {
        const idToken = this.authService.idToken();
        if (idToken) {
            await navigator.clipboard.writeText(idToken)
            AppComponent.messageToast.show({ message: 'ID Token copied to clipboard' });
        }
    }

    protected async copyAccessToken() {
        const token = this.authService.accessToken();
        if (token) {
            await navigator.clipboard.writeText(token);
            AppComponent.messageToast.show({ message: 'Access Token copied to clipboard' });
        }
    }

    protected getDecodedIdToken() {
        const idToken = this.authService.idToken();
        return idToken ? StringUtils.jwtDecode(idToken) : null;
    }

    protected getDecodedAccessToken() {
        const accessToken = this.authService.accessToken();
        return accessToken ? StringUtils.jwtDecode(accessToken) : null;
    }

    async onLocaleChange(value: LocaleInDropDown) {
        this.localeChanged.set(value.key != AppConstants.getUserLocale());
    }

    async onApplyLocaleChange(value: LocaleInDropDown) {
        await changeCulture(value.key)
        AppConstants.setUserLocale(value.key);
        this.localeChanged.set(false);
    }

    async onRefreshToken() {
        await this.authService.refreshTokenSilently(true);
    }

    protected onClearAppSettingsCache() {
        localStorage.clear();
        sessionStorage.clear();
    }
}
